import Heading from '../../components/Heading'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import IconButton from '../../components/IconButton'
import { useFetchSingle } from '../../hooks/useFetchSingle'
import { Customization } from '../../types/Customization'
import { ApiRoutes } from '../../services/ApiService'
import {
    StoredIndividualization,
    StoredTemplate,
} from '../../types/Individualization'
import ArticlePreview from '../../compositions/Fulfillment/Detail/ArticlePreview/ArticlePreview'
import CustomizationPreview from '../../compositions/Fulfillment/Detail/CustomizationPreview/CustomizationPreview'
import CustomizationExport from '../../compositions/Fulfillment/Detail/CustomizationExport/CustomizationExport'
import { fabric } from 'fabric'
import InputDetails from './InputDetails'
import cx from 'classnames'
import styles from '../../compositions/Order/CustomizationDetail.module.css'

const FulfillmentDetail: React.FC<{ customization_id?: number }> = ({
    customization_id,
}) => {
    const { t } = useTranslation(['fulfillment'])
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [activeTemplate, setActiveTemplate] = useState<StoredTemplate | null>(
        null
    )

    const { data: customization, fetch: fetchCustomization } =
        useFetchSingle<Customization>(ApiRoutes.CUSTOMIZATIONS)

    const { data: individualization, fetch: fetchIndividualization } =
        useFetchSingle<StoredIndividualization>(ApiRoutes.INDIVIDUALIZATIONS)

    useEffect(() => {
        if (customization_id) {
            fetchCustomization(customization_id)
        } else if (id) fetchCustomization(id)
    }, [customization_id, id, fetchCustomization])

    useEffect(() => {
        if (customization) {
            fetchIndividualization(customization.individualization_id)
            setActiveTemplate(customization.templates[0] || null)
        }
    }, [fetchIndividualization, customization])

    const templates: StoredTemplate[] = useMemo(() => {
        return (
            customization?.templates.map((template) => {
                template.canvas = new fabric.Canvas(
                    document.createElement('canvas'),
                    {
                        backgroundColor: 'white',
                        selection: false,
                        renderOnAddRemove: true,
                        preserveObjectStacking: true,
                    }
                )
                return template
            }) || []
        )
    }, [customization?.templates])

    return (
        <>            
            {!customization_id && (
                <>
                    <Heading level={2} classes={'zr-mb-4'}>
                        {t('detail.heading')} #{id}
                    </Heading>
                    <IconButton
                        icon={faArrowLeft}
                        color={'black'}
                        onClick={() => navigate(`/fulfillment`)}
                    >
                        {t('detail.back')}
                    </IconButton>
                </>
            )}
            {customization && (
                <div
                    className={'zr-mt-4 zr-d-flex zr-gap-4 zr-w-100'}
                    style={{ flexWrap: 'wrap' }}
                >
                    {(customization_id) != null && (
                        <Heading level={3} classes={'zr-mb-2 zr-w-100'}>
                            {t('detail.heading')} #{customization_id}
                        </Heading>
                    )}
                    <div style={{ minWidth: '500px' }}>
                        <CustomizationPreview
                            activeTemplate={activeTemplate}
                            setActiveTemplate={setActiveTemplate}
                            customization={customization}
                            templates={templates}
                        />
                    </div>
                    <div style={{ flex: 1, flexGrow: 1 }}>
                        <Heading level={3} classes={'zr-mb-2 zr-mt-0'}>
                            {t('detail.export.article')}
                        </Heading>
                        <ArticlePreview
                            article={individualization?.article ?? null}
                        />
                        <CustomizationExport
                            activeTemplate={activeTemplate}
                            customization={customization}
                            templates={templates}
                        />
                        <Heading level={3} classes={'zr-mb-2'}>
                            {t('detail.export.user_inputs')}
                        </Heading>
                        <div
                            className={
                                'zr-d-flex zr-gap-4 zr-flex-wrap zr-mb-4'
                            }
                        >
                            {templates.map((template) => {
                                return (
                                    <div
                                        key={template.id}
                                        className={cx(styles['template-item'])}
                                        style={{
                                            background:
                                                activeTemplate?.id ===
                                                template.id
                                                    ? '#ccc'
                                                    : '#f1f1f1',
                                        }}
                                        onClick={() =>
                                            setActiveTemplate(template)
                                        }
                                    >
                                        {template.name}
                                    </div>
                                )
                            })}
                        </div>
                        <InputDetails
                            inputs={customization.inputs}
                            activeTemplate={activeTemplate}
                        />
                    </div>
                </div>
            )}
        </>
    )
}

export default FulfillmentDetail
